import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Users, Target, Award, Leaf } from 'lucide-react';

const PersonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="160" height="160">
    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
  </svg>
);


const About = () => {
  const values = [
    {
      icon: <Leaf className="w-10 h-10" />,
      title: "Sustainability",
      description: "We're committed to reducing carbon emissions and promoting renewable energy adoption for a cleaner future."
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: "Customer First",
      description: "Your satisfaction is our priority. We provide personalized solutions and exceptional service at every step."
    },
    {
      icon: <Award className="w-10 h-10" />,
      title: "Quality Excellence",
      description: "We use only premium components and certified installers to ensure your system performs at its best."
    },
    {
      icon: <Target className="w-10 h-10" />,
      title: "Innovation",
      description: "We stay ahead with the latest solar technology and best practices to maximize your energy savings."
    }
  ];

  // const team = [
  //   {
  //     name: "Nitin",
  //     role: "Founder",
  //     icon: <PersonIcon />
  //   },
  //   {
  //     name: "Pratik",
  //     role: "Co-Founder",
  //     icon: <PersonIcon />
  //   },
  //   {
  //     name: "Tushar",
  //     role: "Co-Founder",
  //     icon: <PersonIcon />
  //   },
  //   {
  //     name: "Amit",
  //     role: "Lead Engineer",
  //     icon: <PersonIcon />
  //   }
  // ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="font-heading font-bold text-5xl md:text-7xl text-primary mb-6 tracking-tight">
              About Solar Vision Tech
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Leading the renewable energy revolution since 2023
            </p>
          </div>

          <div className="bg-card border border-border/50 shadow-xl rounded-3xl p-8 md:p-12">
            <h2 className="font-heading font-semibold text-3xl text-primary mb-6">Our Story</h2>
            <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                Founded in 2023, Solar Vision Tech was born from a simple belief that clean energy should be
                accessible to everyone. What started as a small team of passionate engineers has grown into
                one of the most trusted solar installation companies in the region.
              </p>
              <p>
                Over the years, we've helped thousands of homeowners and businesses make the transition to
                solar energy, reducing their carbon footprint while saving money on electricity bills. Our
                commitment to quality, innovation, and customer satisfaction has earned us numerous awards
                and, more importantly, the trust of our community.
              </p>
              <p>
                Today, we continue to push the boundaries of what's possible with solar technology, offering
                cutting-edge solutions that maximize efficiency and minimize environmental impact. Every
                installation we complete brings us one step closer to our vision of a sustainable,
                carbon-neutral future.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-8 md:py-8" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-semibold text-3xl md:text-5xl text-primary mb-4 tracking-tight">
              Our Values
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-${index}`}
                className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl p-8 text-center"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 text-primary rounded-2xl mb-6">
                  {value.icon}
                </div>
                <h3 className="font-heading font-semibold text-xl mb-3">{value.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      
      {/* Mission Section */}
      <section className="py-8 md:py-8" data-testid="mission-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-primary rounded-3xl p-12 md:p-20 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/20 rounded-full blur-3xl"></div>

            <div className="relative z-10">
              <h2 className="font-heading font-bold text-3xl md:text-5xl text-primary-foreground mb-6">
                Our Mission
              </h2>
              <p className="text-primary-foreground/90 text-lg md:text-xl leading-relaxed">
                To accelerate the world's transition to sustainable energy by making solar power accessible,
                affordable, and efficient for everyone. We envision a future where clean energy is the norm,
                not the exception, and we're committed to making that vision a reality, one installation at a time.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
